/**
 * Settings read back from storage are whatever an older build (or a hand edit) left there: take each
 * field that is valid, clamp numbers into range, and fall back to the default for the rest.
 */

import { DEFAULT_KEYS, type KeyBindings } from '../input/keyboard';
import type { AudioSettings, DisplaySettings, PlaySettings, Settings } from './index';

const obj = (v: unknown): Record<string, unknown> =>
  (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

const num = (v: unknown, fallback: number, lo: number, hi: number, step = 0): number => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  const c = Math.min(hi, Math.max(lo, v));
  return step > 0 ? Math.round(c / step) * step : c;
};

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

function oneOf<T extends string | number>(v: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.find((a) => a === v) ?? fallback;
}

const codes = (v: unknown, fallback: readonly string[]): readonly string[] => {
  if (!Array.isArray(v)) return fallback;
  const out = v.filter((c): c is string => typeof c === 'string' && /^[A-Za-z0-9]{1,24}$/.test(c));
  return out.length > 0 ? [...new Set(out)].slice(0, 4) : fallback;
};

function bindings(v: unknown, fallback: KeyBindings): KeyBindings {
  const o = obj(v);
  return {
    left: codes(o['left'], fallback.left),
    right: codes(o['right'], fallback.right),
    jump: codes(o['jump'], fallback.jump),
    fire: codes(o['fire'], fallback.fire),
  };
}

/** Any value -> valid settings (defaults where the value is missing or wrong). */
export function sanitize(raw: unknown): Settings {
  const r = obj(raw);
  const a = obj(r['audio']);
  const d = obj(r['display']);
  const p = obj(r['play']);
  const k = Array.isArray(r['keys']) ? (r['keys'] as unknown[]) : [];
  const audio: AudioSettings = {
    music: oneOf(a['music'], ['remastered', '1987', 'off'] as const, 'remastered'),
    musicVolume: num(a['musicVolume'], 0.7, 0, 1),
    effectsVolume: num(a['effectsVolume'], 0.8, 0, 1),
    extraEffects: bool(a['extraEffects'], false),
  };
  const display: DisplaySettings = {
    look: oneOf(d['look'], ['modern', '1987'] as const, 'modern'),
    crt: bool(d['crt'], false),
    paletteLock: num(d['paletteLock'], 0.25, 0, 1),
    pixelSize: oneOf(d['pixelSize'], [1, 2, 3] as const, 1),
    colourAid: bool(d['colourAid'], false),
    reduceFlashing: bool(d['reduceFlashing'], false),
  };
  const play: PlaySettings = { gameSpeed: Math.round(num(p['gameSpeed'], 1, 0.5, 1, 0.1) * 10) / 10 };
  return {
    audio,
    display,
    play,
    keys: [bindings(k[0], DEFAULT_KEYS[0]), bindings(k[1], DEFAULT_KEYS[1])],
  };
}
